import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import StatusBadge from '../components/StatusBadge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Loader2, TrendingUp, TrendingDown, Target, Award, Zap, DollarSign } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';

const COLORS = ['#235894', '#26B8A7', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6'];

const Reporting = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [ideasByStatus, setIdeasByStatus] = useState([]);
  const [projectsByStatus, setProjectsByStatus] = useState([]);
  const [topProjects, setTopProjects] = useState([]);
  const [benefitsSummary, setBenefitsSummary] = useState({});
  const [roiAnalysis, setRoiAnalysis] = useState(null);
  const [ideaRankings, setIdeaRankings] = useState(null);

  useEffect(() => {
    fetchReportingData();
  }, []);

  const fetchReportingData = async () => {
    try {
      const [ideasRes, projectsRes, topRes, benefitsRes, roiRes, rankingsRes] = await Promise.all([
        api.get('/reporting/ideas-by-status'),
        api.get('/reporting/projects-by-status'),
        api.get('/reporting/top-projects'),
        api.get('/reporting/benefits-summary'),
        api.get('/reporting/roi-analysis'),
        api.get('/reporting/idea-rankings')
      ]);
      
      setIdeasByStatus(ideasRes.data);
      setProjectsByStatus(projectsRes.data);
      setTopProjects(topRes.data);
      setBenefitsSummary(benefitsRes.data);
      setRoiAnalysis(roiRes.data);
      setIdeaRankings(rankingsRes.data);
    } catch (error) {
      console.error('Failed to fetch reporting data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    if (!value) return '£0';
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 }).format(value);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-[#235894]" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="reporting-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-slate-900 font-['Plus_Jakarta_Sans']">Reporting</h1>
        <p className="text-slate-500 mt-1">Analytics and insights for your CI initiatives</p>
      </div>

      {/* Benefits Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="bg-gradient-to-br from-[#235894] to-[#1E4B80] text-white">
          <CardContent className="pt-6">
            <p className="text-white/80 text-sm font-medium uppercase tracking-wider">Expected Benefits</p>
            <p className="text-4xl font-bold mt-2 font-['Plus_Jakarta_Sans']">
              {formatCurrency(benefitsSummary.expected)}
            </p>
            <p className="text-white/60 text-sm mt-2">From all approved ideas</p>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-[#26B8A7] to-[#1fa393] text-white">
          <CardContent className="pt-6">
            <p className="text-white/80 text-sm font-medium uppercase tracking-wider">Realised Benefits</p>
            <p className="text-4xl font-bold mt-2 font-['Plus_Jakarta_Sans']">
              {formatCurrency(benefitsSummary.realised)}
            </p>
            <p className="text-white/60 text-sm mt-2">From completed projects</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <p className="text-slate-500 text-sm font-medium uppercase tracking-wider">Gap to Target</p>
            <p className="text-4xl font-bold mt-2 text-amber-600 font-['Plus_Jakarta_Sans']">
              {formatCurrency(benefitsSummary.gap)}
            </p>
            <p className="text-slate-400 text-sm mt-2">Expected - Realised</p>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Ideas by Status */}
        <Card>
          <CardHeader>
            <CardTitle>Ideas by Status</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={ideasByStatus}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="status" tick={{ fontSize: 12 }} />
                  <YAxis />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'white', 
                      border: '1px solid #E2E8F0',
                      borderRadius: '8px'
                    }} 
                  />
                  <Bar dataKey="count" fill="#235894" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Projects by Status */}
        <Card>
          <CardHeader>
            <CardTitle>Projects by Status</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-80 flex items-center justify-center">
              {projectsByStatus.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={projectsByStatus}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={5}
                      dataKey="count"
                      nameKey="status"
                    >
                      {projectsByStatus.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-slate-400">No project data available</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Expected vs Realised */}
      <Card>
        <CardHeader>
          <CardTitle>Expected vs Realised Benefits</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart 
                data={[
                  { name: 'Benefits', expected: benefitsSummary.expected, realised: benefitsSummary.realised }
                ]}
                layout="vertical"
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis type="number" tickFormatter={(value) => `£${(value/1000).toFixed(0)}k`} />
                <YAxis dataKey="name" type="category" width={80} />
                <Tooltip 
                  formatter={(value) => formatCurrency(value)}
                  contentStyle={{ 
                    backgroundColor: 'white', 
                    border: '1px solid #E2E8F0',
                    borderRadius: '8px'
                  }} 
                />
                <Legend />
                <Bar dataKey="expected" name="Expected" fill="#235894" radius={[0, 4, 4, 0]} />
                <Bar dataKey="realised" name="Realised" fill="#26B8A7" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Top 10 Projects */}
      <Card>
        <CardHeader>
          <CardTitle>Top 10 Projects by Expected Benefit</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-96">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={topProjects} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis type="number" tickFormatter={(value) => value.toLocaleString()} />
                <YAxis dataKey="name" type="category" width={200} tick={{ fontSize: 11 }} />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'white', 
                    border: '1px solid #E2E8F0',
                    borderRadius: '8px'
                  }} 
                />
                <Bar dataKey="expected_benefit" name="Expected Benefit" fill="#235894" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* ROI Analysis Section */}
      {roiAnalysis && (
        <Card className="border-2 border-[#26B8A7]">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-[#26B8A7]" />
              ROI Analysis Dashboard
            </CardTitle>
            <CardDescription>
              Automated ROI calculations comparing expected vs realized benefits
            </CardDescription>
          </CardHeader>
          <CardContent>
            {/* ROI Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="p-4 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-2 text-slate-500 text-sm mb-1">
                  <Target className="w-4 h-4" />
                  Total Investment
                </div>
                <p className="text-2xl font-bold text-slate-900">
                  {formatCurrency(roiAnalysis.summary.total_investment)}
                </p>
              </div>
              <div className="p-4 bg-blue-50 rounded-lg">
                <div className="flex items-center gap-2 text-blue-600 text-sm mb-1">
                  <TrendingUp className="w-4 h-4" />
                  Expected Annual
                </div>
                <p className="text-2xl font-bold text-blue-900">
                  {formatCurrency(roiAnalysis.summary.total_expected_annual_benefit)}
                </p>
              </div>
              <div className="p-4 bg-emerald-50 rounded-lg">
                <div className="flex items-center gap-2 text-emerald-600 text-sm mb-1">
                  <Award className="w-4 h-4" />
                  Realized Annual
                </div>
                <p className="text-2xl font-bold text-emerald-900">
                  {formatCurrency(roiAnalysis.summary.total_realized_annual_benefit)}
                </p>
              </div>
              <div className="p-4 bg-purple-50 rounded-lg">
                <div className="flex items-center gap-2 text-purple-600 text-sm mb-1">
                  <Zap className="w-4 h-4" />
                  ROI Multiple
                </div>
                <p className="text-2xl font-bold text-purple-900">
                  {roiAnalysis.summary.overall_roi_multiple || 'N/A'}x
                </p>
              </div>
            </div>

            {/* Benefit Realization Rate */}
            <div className="mb-6 p-4 bg-gradient-to-r from-[#235894]/10 to-[#26B8A7]/10 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Benefit Realization Rate</p>
                  <p className="text-3xl font-bold text-slate-900">
                    {roiAnalysis.summary.benefit_realization_rate}%
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-slate-500">Avg Achievement vs Expected</p>
                  <p className="text-3xl font-bold text-slate-900 flex items-center gap-1 justify-end">
                    {roiAnalysis.summary.avg_achievement_vs_expected > 100 ? (
                      <TrendingUp className="w-6 h-6 text-emerald-500" />
                    ) : (
                      <TrendingDown className="w-6 h-6 text-amber-500" />
                    )}
                    {roiAnalysis.summary.avg_achievement_vs_expected}%
                  </p>
                </div>
              </div>
            </div>

            {/* Project ROI Table */}
            <div className="mt-6">
              <h4 className="font-semibold text-slate-900 mb-3">Project ROI Performance</h4>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead>Project</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Investment</TableHead>
                      <TableHead className="text-right">Expected %</TableHead>
                      <TableHead className="text-right">Actual %</TableHead>
                      <TableHead className="text-right">Variance</TableHead>
                      <TableHead className="text-right">ROI Multiple</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {roiAnalysis.projects.slice(0, 10).map((project) => (
                      <TableRow key={project.id}>
                        <TableCell className="font-medium">{project.name}</TableCell>
                        <TableCell>
                          <StatusBadge status={project.status} />
                        </TableCell>
                        <TableCell className="text-right">
                          {formatCurrency(project.investment)}
                        </TableCell>
                        <TableCell className="text-right">
                          {project.expected_improvement_percent}%
                        </TableCell>
                        <TableCell className="text-right">
                          {project.actual_improvement_percent !== null 
                            ? `${project.actual_improvement_percent}%` 
                            : '-'}
                        </TableCell>
                        <TableCell className="text-right">
                          {project.variance_percent !== null ? (
                            <span className={project.variance_percent >= 0 ? 'text-emerald-600' : 'text-red-600'}>
                              {project.variance_percent >= 0 ? '+' : ''}{project.variance_percent}%
                            </span>
                          ) : '-'}
                        </TableCell>
                        <TableCell className="text-right font-semibold">
                          {project.roi_multiple ? `${project.roi_multiple}x` : '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Idea Rankings Section */}
      {ideaRankings && (
        <Card>
          <CardHeader>
            <CardTitle>Idea Rankings</CardTitle>
            <CardDescription>Ideas ranked by different criteria to help prioritization</CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="quick_wins">
              <TabsList className="mb-4">
                <TabsTrigger value="quick_wins" className="flex items-center gap-1">
                  <Zap className="w-4 h-4" />
                  Quick Wins ({ideaRankings.quick_wins?.length || 0})
                </TabsTrigger>
                <TabsTrigger value="by_benefit">By Benefit</TabsTrigger>
                <TabsTrigger value="by_ease">By Ease</TabsTrigger>
                <TabsTrigger value="by_priority">By Priority</TabsTrigger>
              </TabsList>

              <TabsContent value="quick_wins">
                {ideaRankings.quick_wins?.length > 0 ? (
                  <div className="space-y-2">
                    {ideaRankings.quick_wins.map((idea, index) => (
                      <div key={idea.id} className="flex items-center gap-3 p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                        <span className="w-6 h-6 rounded-full bg-emerald-500 text-white flex items-center justify-center text-sm font-bold">
                          {index + 1}
                        </span>
                        <div className="flex-1">
                          <p className="font-medium text-slate-900">{idea.title}</p>
                          <p className="text-sm text-slate-500">{idea.department} • Impact: {idea.impact_score}/5 • Effort: {idea.effort_score}/5</p>
                        </div>
                        <div className="text-right">
                          <p className="font-semibold text-emerald-700">{idea.improvement_percent}% improvement</p>
                          <StatusBadge status={idea.status} />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-400 text-center py-8">No Quick Wins found. Quick Wins have Impact ≥ 4 and Effort ≤ 2.</p>
                )}
              </TabsContent>

              <TabsContent value="by_benefit">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>#</TableHead>
                      <TableHead>Idea</TableHead>
                      <TableHead>Department</TableHead>
                      <TableHead className="text-right">Expected Improvement</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {ideaRankings.by_benefit?.map((idea, index) => (
                      <TableRow key={idea.id}>
                        <TableCell className="font-bold">{index + 1}</TableCell>
                        <TableCell className="font-medium">{idea.title}</TableCell>
                        <TableCell>{idea.department}</TableCell>
                        <TableCell className="text-right font-semibold text-emerald-600">
                          {idea.improvement_percent}%
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TabsContent>

              <TabsContent value="by_ease">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>#</TableHead>
                      <TableHead>Idea</TableHead>
                      <TableHead>Effort</TableHead>
                      <TableHead>Complexity</TableHead>
                      <TableHead className="text-right">Cost</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {ideaRankings.by_ease?.map((idea, index) => (
                      <TableRow key={idea.id}>
                        <TableCell className="font-bold">{index + 1}</TableCell>
                        <TableCell className="font-medium">{idea.title}</TableCell>
                        <TableCell>
                          <StatusBadge status={idea.effort_estimate || `${idea.effort_score}/5`} />
                        </TableCell>
                        <TableCell>
                          <StatusBadge status={idea.complexity || 'N/A'} />
                        </TableCell>
                        <TableCell className="text-right">
                          {formatCurrency(idea.one_off_cost)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TabsContent>

              <TabsContent value="by_priority">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>#</TableHead>
                      <TableHead>Idea</TableHead>
                      <TableHead className="text-center">Impact</TableHead>
                      <TableHead className="text-center">Effort</TableHead>
                      <TableHead className="text-right">Priority Score</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {ideaRankings.by_priority?.map((idea, index) => (
                      <TableRow key={idea.id}>
                        <TableCell className="font-bold">{index + 1}</TableCell>
                        <TableCell className="font-medium">{idea.title}</TableCell>
                        <TableCell className="text-center">{idea.impact_score}/5</TableCell>
                        <TableCell className="text-center">{idea.effort_score}/5</TableCell>
                        <TableCell className="text-right font-semibold text-[#235894]">
                          {idea.priority_score}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default Reporting;
